import { useQuery } from "@tanstack/react-query";
import { useFullPath } from "raviger";

import query from "@/Utils/request/query";
import useAuthUser from "@/hooks/useAuthUser";
import facilityApi from "@/types/facility/facilityApi";

const extractFacilityId = (path: string) => {
  const segments = path.split("/");

  if (segments[1] === "facility" && segments[2]) {
    return segments[2];
  }

  throw new Error("'useCurrentFacility' must be used within a facility route");
};

/**
 * Avoids fetching the facility data on all places the current facility is needed.
 *
 * @returns The current facility in context.
 */
export default function useCurrentFacility() {
  const path = useFullPath();
  const facilityId = extractFacilityId(path);
  // Include current user in query key and only fetch when user is available
  // to avoid serving facility data (which includes user-scoped permissions)
  // cached for a different user.
  const user = useAuthUser();
  const { data: facility, isLoading: isFacilityLoading } = useQuery({
    queryKey: ["facility", facilityId, user?.id],
    queryFn: query(facilityApi.get, {
      pathParams: { facilityId: facilityId ?? "" },
    }),
    staleTime: 1000 * 60 * 5, // cache for 5 minutes
    refetchOnMount: "always",
    enabled: !!facilityId && !!user,
  });

  return { facilityId, facility, isFacilityLoading };
}

export function useCurrentFacilitySilently() {
  try {
    return useCurrentFacility();
  } catch {
    return { facilityId: undefined, facility: undefined };
  }
}
